/**
 * Welle 9 (pre87/pre87c): Session-Dokumente (Scans, signierte PDFs, EC-Karten-Protokolle)
 * werden AUSSCHLIESSLICH lokal im Browser des Beraters gespeichert (IndexedDB) — niemals
 * an den Server gesendet. Während des Calls erfasst, in der Nachbearbeitung herunterladbar.
 * Pro Call (callId) abgelegt. Inhalte als Blob (Bild/PDF/Text).
 */
const DB_NAME = 'bamoa-wrapup-docs';
const STORE = 'docs';
const VERSION = 1;

export type DocKind = 'scan' | 'signed' | 'nfc';

export interface LocalDoc {
  docId: string;
  callId: string;
  kind: DocKind;
  name: string;
  mime: string;
  size: number;
  blob: Blob;
  at: number;
  downloaded?: boolean; // pre87e: heruntergeladen = abgelegt → keine Logout-Warnung mehr
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'docId' });
        os.createIndex('callId', 'callId', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function uuid(): string {
  try { return crypto.randomUUID(); } catch { return 'd_' + Date.now() + '_' + Math.random().toString(36).slice(2); }
}

// pre87d: Gesamtzahl noch lokal liegender (nicht abgelegter) Dokumente — synchron lesbar
// für die beforeunload-/Logout-Warnung. Wird nach jedem Schreib-/Löschvorgang aktualisiert.
let pendingCount = 0;
export function getPendingDocCountSync(): number { return pendingCount; }
export async function refreshPendingDocCount(): Promise<number> {
  try {
    const db = await openDb();
    const all = await new Promise<LocalDoc[]>((res, rej) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => res((req.result as LocalDoc[]) || []);
      req.onerror = () => rej(req.error);
    });
    db.close();
    pendingCount = all.filter((d) => !d.downloaded).length;
    return pendingCount;
  } catch { return pendingCount; }
}

/** pre87e: Dokument als heruntergeladen (= abgelegt) markieren → zählt nicht mehr als offen. */
export async function markDownloaded(docId: string): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((res, rej) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      const getReq = store.get(docId);
      getReq.onsuccess = () => {
        const rec = getReq.result as LocalDoc | undefined;
        if (rec && !rec.downloaded) { rec.downloaded = true; store.put(rec); }
      };
      tx.oncomplete = () => res();
      tx.onerror = () => rej(tx.error);
    });
    db.close();
    void refreshPendingDocCount();
  } catch { /* */ }
}

/** Ein Session-Dokument lokal sichern. Dedupe über optionale stabile docId. */
export async function saveLocalDoc(
  callId: string,
  doc: { kind: DocKind; name: string; mime: string; blob: Blob; docId?: string },
): Promise<LocalDoc> {
  const rec: LocalDoc = {
    docId: doc.docId ?? uuid(),
    callId,
    kind: doc.kind,
    name: doc.name,
    mime: doc.mime,
    size: doc.blob.size,
    blob: doc.blob,
    at: Date.now(),
    downloaded: false,
  };
  const db = await openDb();
  await new Promise<void>((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(rec); // put = idempotent bei gleicher docId
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
  db.close();
  void refreshPendingDocCount();
  void autoSaveToTarget(rec);
  return rec;
}

/**
 * pre89c: Sobald ein Dokument erfasst wird (Scan/Signatur/EC), versuchen wir es SOFORT in den
 * für seinen Typ konfigurierten Ordner zu legen (mit Metadaten-Stempel + bereinigtem Namen).
 * Best-effort: ohne konfigurierten Ordner oder ohne erteilte Berechtigung bleibt es in der
 * lokalen Ablage und kann in der Nachbearbeitung manuell gespeichert werden. Kein Nutzer-
 * Gesten-Kontext → interactive:false (es wird keine Berechtigung neu angefragt).
 */
async function autoSaveToTarget(rec: LocalDoc): Promise<void> {
  // pre89d: Scan & Signatur werden NICHT mehr automatisch abgelegt — der Berater prüft sie
  // erst im Viewer und bestätigt dort die Ablage. Nur das EC-Protokoll (generierte Daten,
  // keine Sichtprüfung nötig) wird direkt bei Erhalt in seinen Ordner gelegt.
  if (rec.kind !== 'nfc') return;
  try {
    const { stampForExport } = await import('./docMeta');
    const { saveToDir } = await import('./fileTargets');
    const { blob, name } = await stampForExport(rec);
    const res = await saveToDir(rec.kind, name, blob, { interactive: false });
    if (res.ok) await markDownloaded(rec.docId);
  } catch { /* best-effort */ }
}

/** Scan/PDF aus einer (noch gültigen) Blob-/Object-URL erfassen. */
export async function captureFromUrl(
  callId: string,
  url: string,
  meta: { kind: DocKind; name: string; mime: string; docId?: string },
): Promise<void> {
  try {
    const blob = await fetch(url).then((r) => r.blob());
    await saveLocalDoc(callId, { ...meta, blob });
  } catch (e) {
    console.warn('[localDocs] captureFromUrl fehlgeschlagen', e);
  }
}

/** Fertigen Blob (z. B. generiertes PDF) in die Ablage übernehmen. */
export async function captureBlob(
  callId: string,
  blob: Blob,
  meta: { kind: DocKind; name: string; mime: string; docId?: string },
): Promise<void> {
  try {
    await saveLocalDoc(callId, { ...meta, blob });
  } catch (e) {
    console.warn('[localDocs] captureBlob fehlgeschlagen', e);
  }
}

/** EC-Karten-Protokoll als Textdokument erfassen. */
export async function captureText(
  callId: string,
  text: string,
  meta: { kind: DocKind; name: string; docId?: string },
): Promise<void> {
  try {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    await saveLocalDoc(callId, { ...meta, mime: 'text/plain', blob });
  } catch (e) {
    console.warn('[localDocs] captureText fehlgeschlagen', e);
  }
}

export async function listLocalDocs(callId: string): Promise<LocalDoc[]> {
  const db = await openDb();
  const out = await new Promise<LocalDoc[]>((res, rej) => {
    const tx = db.transaction(STORE, 'readonly');
    const idx = tx.objectStore(STORE).index('callId');
    const req = idx.getAll(IDBKeyRange.only(callId));
    req.onsuccess = () => res((req.result as LocalDoc[]) || []);
    req.onerror = () => rej(req.error);
  });
  db.close();
  return out.sort((a, b) => a.at - b.at);
}

export async function deleteLocalDoc(docId: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(docId);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
  db.close();
  void refreshPendingDocCount();
}

export async function countLocalDocs(callId: string): Promise<number> {
  try { return (await listLocalDocs(callId)).length; } catch { return 0; }
}
